import React, { useState, useRef } from 'react';
import { X, Clock, User, Calendar, MessageSquare, BarChart3 } from 'lucide-react';
import { RecruiterCandidate } from '../../../types/recruiter';
import { ConversationLog } from './ConversationLog';
import { useConversationLog } from '../../../hooks/useConversationLog';
import { safeToInterviewTypeLabel } from '../../../types/backendEnums';
import { useReactToPrint } from 'react-to-print';
import { EvaluationDetail } from './EvaluationDetail';

interface EvaluationSidebarProps {
  showEvaluationSidebar: boolean;
  setShowEvaluationSidebar: (show: boolean) => void;
  selectedCandidate: RecruiterCandidate | null;
  selectedInterviewIndex: number;
  setSelectedInterviewIndex: (index: number) => void;
  onScheduleInterview?: () => void;
  onEditCandidate?: () => void;
  onDeleteCandidate?: () => void;
}

export const EvaluationSidebar: React.FC<EvaluationSidebarProps> = ({
  showEvaluationSidebar,
  setShowEvaluationSidebar,
  selectedCandidate,
  selectedInterviewIndex,
  setSelectedInterviewIndex,
  onScheduleInterview,
  onEditCandidate,
  onDeleteCandidate,
}) => {
  const [activeTab, setActiveTab] = useState<'evaluation' | 'conversation'>('evaluation');
  // 選択された面接の評価情報を取得（フック呼び出し前に計算）
  const selectedInterview = selectedCandidate?.interviews?.[selectedInterviewIndex];
  const evaluation = selectedInterview?.evaluation || selectedCandidate?.evaluation;
  const printAreaRef = useRef<HTMLDivElement>(null);

  const handlePrint = useReactToPrint({
    contentRef: printAreaRef,
    documentTitle: `面接結果_${selectedCandidate?.name || ''}_${selectedInterview?.date || ''}`,
    pageStyle: `@page { size: A4; margin: 12mm; } @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }`,
    onBeforePrint: async () => {
      if (activeTab !== 'evaluation') {
        setActiveTab('evaluation');
        await new Promise((resolve) => setTimeout(resolve, 150));
      }
    },
  });

  // 会話ログフック（常に呼び出し）
  const {
    conversationLog,
    hasLog,
    loading: conversationLoading,
    error: conversationError,
    refetch,
  } = useConversationLog(selectedInterview?.sessionId || null);

  if (!showEvaluationSidebar) return null;
  if (!selectedCandidate) return null;

  // selectedCandidateが文字列の場合はエラー状態として扱う
  if (typeof selectedCandidate === 'string') {
    console.error('🚨 selectedCandidate is a string, expected object:', selectedCandidate);
    return (
      <div className="fixed right-0 top-0 h-full w-full max-w-2xl bg-white shadow-2xl z-50 p-6">
        <div className="text-center">
          <h2 className="text-xl font-bold text-red-600 mb-4">データエラー</h2>
          <p className="text-gray-600 mb-4">候補者データの形式が正しくありません。</p>
          <button
            onClick={() => setShowEvaluationSidebar(false)}
            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
          >
            閉じる
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* オーバーレイ */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300"
        onClick={() => setShowEvaluationSidebar(false)}
      />

      {/* サイドバー */}
      <div className="fixed right-0 top-0 h-full w-full max-w-2xl bg-white shadow-2xl z-50 transform translate-x-0 transition-transform duration-300 ease-in-out overflow-y-auto">
        {/* ヘッダー */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 z-10">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">
              候補者詳細 - {selectedCandidate?.name || '不明'}
            </h2>
            <button
              onClick={() => setShowEvaluationSidebar(false)}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* コンテンツ */}
        <div className="p-6 space-y-6 print-container" ref={printAreaRef}>
          {/* 候補者基本情報（固定情報のみ） */}
          <div className="bg-gray-50 rounded-lg p-6">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{selectedCandidate.name}</h3>
                <p className="text-gray-600">{selectedCandidate.position}</p>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-4 mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">メール</label>
                  <p className="text-sm text-gray-900">{selectedCandidate.email}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">電話番号</label>
                  <p className="text-sm text-gray-900">{selectedCandidate.phone || '-'}</p>
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-gray-500">担当者</label>
                  <div className="text-sm text-gray-900 flex flex-wrap gap-2 mt-1">
                    {Array.isArray(selectedCandidate.assignees) &&
                    selectedCandidate.assignees.length > 0 ? (
                      selectedCandidate.assignees.map((u, idx) => {
                        const label = u.displayName || u.email || u.id || '';
                        if (!label) return null;
                        return (
                          <span
                            key={u.id || idx}
                            className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-md border border-blue-100"
                          >
                            {label}
                          </span>
                        );
                      })
                    ) : (
                      <span className="text-gray-500">未設定</span>
                    )}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">ステータス</label>
                  <span
                    className={`px-2 py-1 text-xs leading-5 font-semibold rounded-full ${
                      selectedCandidate.status === 'completed'
                        ? 'bg-green-100 text-green-800'
                        : selectedCandidate.status === 'pending'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {selectedCandidate.status === 'completed'
                      ? '面接完了'
                      : selectedCandidate.status === 'pending'
                        ? '面接予定'
                        : selectedCandidate.status === 'invited'
                          ? '招待済み'
                          : selectedCandidate.status}
                  </span>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">招待日</label>
                  <p className="text-sm text-gray-900">{selectedCandidate.inviteDate}</p>
                </div>
              </div>
            </div>
          </div>

          {/* 候補者アクション */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 no-print">
            <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-100">
              <User className="text-gray-600" size={20} />
              <h4 className="text-lg font-semibold text-gray-900">候補者アクション</h4>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <button
                onClick={onScheduleInterview}
                className="bg-green-600 text-white py-2 px-3 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2 text-sm"
              >
                <Clock size={14} />
                日程調整
              </button>
              <button
                onClick={onEditCandidate}
                className="bg-green-100 text-green-700 py-2 px-3 rounded-lg hover:bg-green-200 transition-colors flex items-center justify-center gap-2 text-sm"
              >
                <User size={14} />
                編集
              </button>
              <button
                onClick={onDeleteCandidate}
                className="bg-red-100 text-red-700 py-2 px-3 rounded-lg hover:bg-red-200 transition-colors flex items-center justify-center gap-2 text-sm"
              >
                <X size={14} />
                削除
              </button>
            </div>
          </div>

          {/* セクション区切り */}
          <div className="py-6">
            <div className="border-t border-gray-300"></div>
          </div>

          {/* 面接情報 */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-100">
              <Calendar className="text-gray-600" size={20} />
              <h4 className="text-lg font-semibold text-gray-900">面接情報</h4>
            </div>

            {selectedCandidate.interviews.length > 0 ? (
              <div className="space-y-3">
                {selectedCandidate.interviews.map((interview, index) => (
                  <div
                    key={index}
                    className={`rounded-lg p-4 border cursor-pointer transition-colors ${
                      selectedInterviewIndex === index
                        ? 'bg-blue-50 border-blue-200 ring-2 ring-blue-100'
                        : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                    }`}
                    onClick={() => setSelectedInterviewIndex(index)}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-3 h-3 rounded-full ${
                            selectedInterviewIndex === index ? 'bg-blue-600' : 'bg-gray-300'
                          }`}
                        />
                        <div className="text-sm font-medium text-gray-900">
                          {safeToInterviewTypeLabel(interview.type)}
                        </div>
                      </div>
                      <div
                        className={`px-2 py-1 text-xs rounded-full ${
                          interview.status === 'completed'
                            ? 'bg-green-100 text-green-800'
                            : interview.status === 'in_progress'
                              ? 'bg-blue-100 text-blue-800'
                              : interview.status === 'scheduled'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {interview.status === 'completed'
                          ? '完了'
                          : interview.status === 'in_progress'
                            ? '進行中'
                            : interview.status === 'scheduled'
                              ? '予定'
                              : interview.status === 'cancelled'
                                ? 'キャンセル'
                                : '未開始'}
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 ml-6 space-y-1">
                      <div>
                        面接可能期間: {interview.date}
                        {interview.endDate && interview.endDate !== interview.date && (
                          <span> 〜 {interview.endDate}</span>
                        )}
                      </div>
                      <div>
                        面接完了日時: {interview.completedAt
                          ? new Date(interview.completedAt).toLocaleString('ja-JP', {
                              month: 'numeric',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })
                          : '-'}
                      </div>
                      {interview.evaluation && (
                        <span className="text-green-600 font-medium">
                          総合スコア: {interview.evaluation.totalScore}~点
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Calendar className="mx-auto text-gray-300 mb-3" size={48} />
                <p className="text-gray-500 text-sm">まだ面接が実施されていません</p>
                <p className="text-gray-400 text-xs mt-1">
                  「日程調整」ボタンから面接を設定してください
                </p>
              </div>
            )}
          </div>

          <div className="text-right">
            <button
              onClick={() => handlePrint()}
              className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm no-print"
            >
              PDF出力
            </button>
          </div>

          {/* 選択された面接の詳細 - タブナビゲーションを含む */}
          {selectedInterview && (
            <>
              {/* タブナビゲーション */}
              <div className="flex space-x-1 bg-gray-100 rounded-lg p-1 no-print">
                <button
                  onClick={() => setActiveTab('evaluation')}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    activeTab === 'evaluation'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  <BarChart3 className="w-4 h-4" />
                  評価結果
                </button>
                <button
                  onClick={() => setActiveTab('conversation')}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    activeTab === 'conversation'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  <MessageSquare className="w-4 h-4" />
                  会話ログ
                  {hasLog && (
                    <span className="bg-blue-100 text-blue-600 text-xs px-2 py-0.5 rounded-full">
                      {conversationLog.length}
                    </span>
                  )}
                </button>
              </div>

              {/* 面接録画リンク */}
              {selectedInterview?.status === 'completed' &&
                selectedInterview?.googleDriveFolderUrl && (
                  <div className="mb-6 no-print">
                    <a
                      href={selectedInterview.googleDriveFolderUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 text-sm font-medium"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                        <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
                      </svg>
                      面接録画を確認する
                    </a>
                  </div>
                )}

              {/* タブコンテンツ */}
              {activeTab === 'conversation' ? (
                <ConversationLog
                  conversationLog={conversationLog}
                  loading={conversationLoading}
                  error={conversationError}
                  onRefetch={refetch}
                />
              ) : (
                <EvaluationDetail
                  selectedInterview={selectedInterview}
                  selectedCandidate={selectedCandidate}
                  evaluation={evaluation}
                />
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};
